import DocumentTypeFields, {
    type IDocumentTypeFieldsValue,
    isDocumentTypeFieldsValid,
} from "components/shared/DocumentTypeFields";
import ModalBase from "components/shared/ModalBase";
import StyledButton from "components/shared/StyledButton";
import type { ITaxProfile } from "helpers/beneficiaryDocuments";
import { expectedTypes, toDateOnly } from "helpers/beneficiaryDocuments";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IBeneficiaryDocumentType } from "store/beneficiaryDocumentTypesStore";
import type { IDocument } from "store/documentStore";

interface IQualifyDocumentModal {
    document: IDocument | undefined;
    documentTypes: IBeneficiaryDocumentType[];
    /** Owner of the document: the types expected from them come first and are preselected */
    beneficiary?: ITaxProfile;
    onClose: () => void;
    onSave: (value: IDocumentTypeFieldsValue) => Promise<void>;
}

/** Assigns a type and a validity period to a document (a pending upload becomes valid). */
const QualifyDocumentModal = ({ document, documentTypes, beneficiary, onClose, onSave }: IQualifyDocumentModal) => {
    const [t] = useTranslation();
    const [value, setValue] = useState<IDocumentTypeFieldsValue>({
        documentTypeId:
            document?.documentTypeId ??
            expectedTypes(documentTypes, beneficiary)[0]?.id ??
            documentTypes[0]?.id ??
            null,
        validFrom: toDateOnly(document?.validFrom),
        validUntil: toDateOnly(document?.validUntil),
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(value);
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalBase
            open={!!document}
            onClose={onClose}
            modalTitle={t("BeneficiaryDocuments.Documents.QualifyTitle")}
            modalSubtitle={document?.name}
        >
            <div className="flex flex-col gap-6">
                <DocumentTypeFields
                    documentTypes={documentTypes}
                    value={value}
                    onChange={setValue}
                    requireType
                    beneficiary={beneficiary}
                />
                <div className="flex justify-end gap-2">
                    <StyledButton buttonType="outlined" onClick={onClose}>
                        {t("BeneficiaryDocuments.Form.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!value.documentTypeId || !isDocumentTypeFieldsValid(value, documentTypes)}
                    >
                        {t("BeneficiaryDocuments.Documents.Validate")}
                    </StyledButton>
                </div>
            </div>
        </ModalBase>
    );
};

export default QualifyDocumentModal;
